import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone, TableWrap } from '../components/ui.tsx';
import { locale, t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { dateTime, day, since } from '../lib/format.ts';
import { shrinkPhoto } from '../lib/images.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { Blocks, imageUrl, RichText, type ShownBlock, type SiteChrome, type SiteEnv } from '../site/blocks.tsx';
import { TONES, useAction, useSave } from './manage/common.tsx';
import { Bars, Kpi, Table } from './Reports.tsx';

/**
 * Website: the business's own site. Pages are put together from blocks (a banner, text,
 * features, pictures, questions, what customers say, the latest posts, a contact form) and
 * previewed exactly as visitors will see them; the blog, the forum with its waiting posts,
 * and the messages from the contact form are handled here too.
 */

type Tab = 'overview' | 'pages' | 'blog' | 'forum' | 'messages' | 'settings';
type BlockKind = ShownBlock['kind'];

interface Settings {
  id: string | null;
  enabled: boolean;
  title: string | null;
  tagline: string | null;
  accent: (typeof TONES)[number];
  footer: string | null;
  hours: string | null;
  blog: boolean;
  forum: boolean;
  moderate: boolean;
  contact_form: boolean;
  show_shop: boolean;
  show_careers: boolean;
  facebook: string | null;
  instagram: string | null;
  tiktok: string | null;
  linkedin: string | null;
}
interface Overview {
  settings: Settings;
  business: string;
  series: { day: string; views: number }[];
  week: number;
  week_before: number;
  month: number;
  top: { path: string; views: number }[];
  counts: { pages: number; published: number; posts: number; drafts: number; messages: number; waiting: number; topics: number };
  recent: { id: string; number: number; name: string; subject: string | null; body: string; created_at: string }[];
  can: { edit: boolean; publish: boolean; moderate: boolean; messages: boolean; settings: boolean };
}
interface PageRow {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  published: boolean;
  in_menu: boolean;
  is_home: boolean;
  sequence: number;
  updated_at: string;
  updated_by_name: string | null;
  blocks: number;
  views_30: number;
}
/** A block as it is edited: pictures by their id, the latest posts without the posts. */
type EditBlock = Record<string, any> & { kind: BlockKind };
interface PageFull {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  blocks: EditBlock[];
  published: boolean;
  in_menu: boolean;
  is_home: boolean;
}
interface ImageRow {
  id: string;
  name: string;
  version: string;
  width: number | null;
  height: number | null;
  bytes: number;
  created_at: string;
  used: number;
}
interface PostRow {
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  cover: string | null;
  cover_version: string | null;
  tags: string[];
  state: 'draft' | 'published';
  published_at: string | null;
  views: number;
  updated_at: string;
  author_name: string | null;
}
interface PostFull {
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  body: string;
  cover: string | null;
  cover_version: string | null;
  tags: string[];
  state: 'draft' | 'published';
  published_at: string | null;
}
interface TopicRow {
  id: string;
  number: number;
  title: string;
  name: string;
  state: 'pending' | 'open' | 'closed' | 'hidden';
  pinned: boolean;
  solved: boolean;
  replies: number;
  views: number;
  last_at: string;
  created_at: string;
  staff_name: string | null;
  waiting: number;
}
interface TopicFull extends TopicRow {
  body: string;
  email: string | null;
  replies_list: {
    id: string;
    body: string;
    name: string;
    email: string | null;
    state: 'pending' | 'visible' | 'hidden';
    answer: boolean;
    created_at: string;
    staff: string | null;
    staff_name: string | null;
  }[];
  can_moderate: boolean;
}
interface MessageRow {
  id: string;
  number: number;
  name: string;
  email: string | null;
  phone: string | null;
  subject: string | null;
  body: string;
  page: string | null;
  state: 'new' | 'handled' | 'spam';
  handled_by_name: string | null;
  handled_at: string | null;
  lead: string | null;
  created_at: string;
}

const BLOCK_ICON: Record<BlockKind, IconName> = {
  hero: 'sparkle',
  text: 'note',
  features: 'star',
  image_text: 'image',
  gallery: 'layers',
  cta: 'chevronRight',
  faq: 'list',
  testimonials: 'chat',
  posts: 'book',
  contact: 'mail',
};
const BLOCK_KINDS = Object.keys(BLOCK_ICON) as BlockKind[];
const FEATURE_ICONS: IconName[] = [
  'star',
  'users',
  'clock',
  'truck',
  'phone',
  'mail',
  'gift',
  'chef',
  'flame',
  'globe',
  'pin',
  'store',
  'shield',
  'sparkle',
  'calendar',
  'bed',
  'car',
  'check',
  'cash',
  'card',
];
const MAX_IMAGE = 600_000;

const siteUrl = (tenant: string, path = '') => `${window.location.origin}/site/${tenant}${path}`;
/** An address from a title: small letters, digits and dashes. */
export const slugify = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/ë/g, 'e')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

/** Blocks get a key that lives only on this screen, so a block keeps its open or closed state while moving. */
let blockSeq = 0;
const keyed = (b: EditBlock, fresh = false): EditBlock => ({ ...b, _k: ++blockSeq, _new: fresh });
const keyedPage = (p: PageFull): PageFull => ({ ...p, blocks: p.blocks.map((b) => keyed(b)) });

function newBlock(kind: BlockKind): EditBlock {
  return keyed(emptyBlock(kind), true);
}

function emptyBlock(kind: BlockKind): EditBlock {
  switch (kind) {
    case 'hero':
      return { kind, heading: '', text: '', button_label: '', button_link: '', image: null };
    case 'text':
      return { kind, heading: '', text: '' };
    case 'features':
      return { kind, heading: '', items: [{ icon: 'star', title: '', text: '' }] };
    case 'image_text':
      return { kind, heading: '', text: '', image: null, side: 'left', button_label: '', button_link: '' };
    case 'gallery':
      return { kind, heading: '', images: [] };
    case 'cta':
      return { kind, heading: '', text: '', button_label: '', button_link: '' };
    case 'faq':
      return { kind, heading: '', items: [{ q: '', a: '' }] };
    case 'testimonials':
      return { kind, heading: '', items: [{ quote: '', name: '' }] };
    case 'posts':
      return { kind, heading: '', count: 3 };
    case 'contact':
      return { kind, heading: '', text: '' };
  }
}

export function Website() {
  const [params, setParams] = useSearchParams();
  const { data: o, reload } = useAction<Overview>('website.overview');
  const { tenant } = useSession();
  const slug = tenant?.slug ?? '';
  const tabs: { key: Tab; icon: IconName; show: boolean; badge?: number }[] = o
    ? [
        { key: 'overview', icon: 'chart', show: true },
        { key: 'pages', icon: 'layout', show: true },
        { key: 'blog', icon: 'book', show: true, badge: o.counts.drafts },
        { key: 'forum', icon: 'chat', show: true, badge: o.counts.waiting },
        { key: 'messages', icon: 'inbox', show: o.can.messages, badge: o.counts.messages },
        { key: 'settings', icon: 'settings', show: true },
      ]
    : [];
  const asked = params.get('tab');
  const tab = (tabs.some((x) => x.key === asked && x.show) ? asked : 'overview') as Tab;
  const editing = { page: params.get('page'), post: params.get('post'), topic: params.get('topic') };
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) v === null ? p.delete(k) : p.set(k, v);
    setParams(p, { replace: false });
  };
  const open = editing.page || editing.post || editing.topic;
  return (
    <Shell>
      <div className="page reports web-page">
        <div className="page-head">
          <div>
            <h1>{t('web.title')}</h1>
            <p className="muted">{t('web.subtitle')}</p>
          </div>
          {o && o.settings.enabled && slug ? (
            <a className="btn btn-ghost" href={siteUrl(slug)} target="_blank" rel="noopener noreferrer">
              <Icon name="external" size={18} />
              <span className="btn-label">{t('web.view_site')}</span>
            </a>
          ) : null}
        </div>
        {!o ? (
          <Skeleton h={110} r="var(--radius-lg)" />
        ) : (
          <>
            {open ? null : (
              <div className="segmented tabs" role="tablist" aria-label={t('web.sections')}>
                {tabs
                  .filter((x) => x.show)
                  .map((x) => (
                    <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => set({ tab: x.key === 'overview' ? null : x.key })}>
                      <Icon name={x.icon} size={16} />
                      {t(`web.tab_${x.key}` as MessageKey)}
                      {x.badge ? <span className="count">{x.badge}</span> : null}
                    </button>
                  ))}
              </div>
            )}
            {editing.page ? (
              <PageEditor
                key={editing.page}
                id={editing.page === 'new' ? null : editing.page}
                o={o}
                slug={slug}
                onClose={() => set({ page: null })}
                onSaved={reload}
                onCreated={(id) => set({ page: id })}
              />
            ) : editing.post ? (
              <PostEditor
                key={editing.post}
                id={editing.post === 'new' ? null : editing.post}
                o={o}
                slug={slug}
                onClose={() => set({ post: null })}
                onSaved={reload}
                onCreated={(id) => set({ post: id })}
              />
            ) : editing.topic ? (
              <TopicDetail key={editing.topic} id={editing.topic} slug={slug} enabled={o.settings.enabled} onClose={() => set({ topic: null })} onChange={reload} />
            ) : tab === 'overview' ? (
              <OverviewTab o={o} slug={slug} go={set} onChange={reload} />
            ) : tab === 'pages' ? (
              <PagesTab o={o} slug={slug} onOpen={(id) => set({ page: id })} onChange={reload} />
            ) : tab === 'blog' ? (
              <BlogTab o={o} slug={slug} onOpen={(id) => set({ post: id })} />
            ) : tab === 'forum' ? (
              <ForumTab o={o} onOpen={(id) => set({ topic: id })} onChange={reload} />
            ) : tab === 'messages' ? (
              <MessagesTab onChange={reload} />
            ) : (
              <SettingsTab o={o} slug={slug} onSaved={reload} />
            )}
          </>
        )}
      </div>
    </Shell>
  );
}

// ---- overview ------------------------------------------------------------------------------

function pct(now: number, before: number) {
  return before ? Math.round(((now - before) / before) * 100) : null;
}
const shortDay = (d: string) => new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'short' }).format(new Date(`${d}T12:00:00Z`));

function OverviewTab({ o, slug, go, onChange }: { o: Overview; slug: string; go: (next: Record<string, string | null>) => void; onChange: () => void }) {
  const { run, busy } = useSave();
  if (!o.counts.pages) {
    return (
      <div className="card web-empty">
        <EmptyState
          icon="globe"
          title={t('web.start_title')}
          body={t('web.start_body', { name: o.business })}
          action={
            o.can.edit ? (
              <Button icon="sparkle" busy={busy} onClick={() => void run(() => action('website.setup', {}), t('web.started')).then((ok) => ok && (onChange(), go({ tab: 'pages' })))}>
                {t('web.start')}
              </Button>
            ) : undefined
          }
        />
      </div>
    );
  }
  const c = o.counts;
  return (
    <>
      <section className={`web-status card${o.settings.enabled ? ' on' : ''}`}>
        <span className={`web-status-dot${o.settings.enabled ? ' on' : ''}`} aria-hidden="true" />
        <div className="web-status-text">
          <strong>{o.settings.enabled ? t('web.status_on') : t('web.status_off')}</strong>
          {o.settings.enabled ? <code className="web-url">{siteUrl(slug)}</code> : <span className="muted">{t('web.status_off_hint')}</span>}
        </div>
        <div className="web-status-actions">
          {o.settings.enabled ? <CopyButton text={siteUrl(slug)} /> : null}
          {o.can.settings ? (
            <Button variant={o.settings.enabled ? 'ghost' : 'primary'} icon="settings" onClick={() => go({ tab: 'settings' })}>
              {o.settings.enabled ? t('web.settings') : t('web.switch_on')}
            </Button>
          ) : null}
        </div>
      </section>
      <section className="rep-kpis" aria-label={t('web.at_a_glance')}>
        <Kpi label={t('web.k_visits')} value={o.week.toLocaleString(locale())} delta={pct(o.week, o.week_before)} hint={tn('web.k_month', o.month)} />
        <Kpi label={t('web.k_pages')} value={String(c.published)} hint={tn('web.k_pages_all', c.pages)} />
        <Kpi label={t('web.k_posts')} value={String(c.posts)} hint={c.drafts ? tn('web.k_drafts', c.drafts) : t('web.k_no_drafts')} />
        {o.can.messages ? (
          <Kpi label={t('web.k_messages')} value={String(c.messages)} hint={c.messages ? t('web.k_to_answer') : t('web.k_all_answered')} warn={c.messages > 0} />
        ) : (
          <Kpi label={t('web.k_topics')} value={String(c.topics)} hint={tn('web.k_waiting', c.waiting)} />
        )}
      </section>
      <div className="web-grid">
        <section className="card">
          <header className="card-head">
            <h2>{t('web.visits_30')}</h2>
            <span className="muted">{t('web.visits_note')}</span>
          </header>
          {o.month ? (
            <Bars
              items={o.series.map((d) => {
                const wd = new Date(`${d.day}T12:00:00Z`).getUTCDay();
                return { key: d.day, label: shortDay(d.day), value: d.views, tip: `${day(d.day)}: ${tn('web.n_views', d.views)}`, weekend: wd === 0 || wd === 6 };
              })}
            />
          ) : (
            <p className="muted">{t('web.no_visits')}</p>
          )}
        </section>
        <section className="card">
          <header className="card-head">
            <h2>{t('web.top_pages')}</h2>
          </header>
          {o.top.length ? (
            <ol className="web-top">
              {o.top.map((p) => (
                <li key={p.path}>
                  <span className="web-top-path">{p.path === '/' ? t('web.home') : p.path}</span>
                  <span className="web-top-bar" aria-hidden="true">
                    <span style={{ width: `${(p.views / o.top[0]!.views) * 100}%` }} />
                  </span>
                  <strong>{p.views.toLocaleString(locale())}</strong>
                </li>
              ))}
            </ol>
          ) : (
            <p className="muted">{t('web.no_visits')}</p>
          )}
        </section>
      </div>
      <div className="web-grid">
        {o.can.messages ? (
          <section className="card">
            <header className="card-head">
              <h2>{t('web.new_messages')}</h2>
              <Button size="sm" variant="ghost" onClick={() => go({ tab: 'messages' })}>
                {t('web.see_all')}
              </Button>
            </header>
            {o.recent.length ? (
              <ul className="web-list">
                {o.recent.map((m) => (
                  <li key={m.id}>
                    <button type="button" onClick={() => go({ tab: 'messages' })}>
                      <span className="web-avatar" aria-hidden="true">
                        {m.name.charAt(0).toUpperCase()}
                      </span>
                      <span className="web-list-main">
                        <strong>{m.subject || m.name}</strong>
                        <span className="muted">{m.body}</span>
                      </span>
                      <span className="muted small">{since(m.created_at)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">{t('web.no_new_messages')}</p>
            )}
          </section>
        ) : null}
        <section className="card">
          <header className="card-head">
            <h2>{t('web.to_do')}</h2>
          </header>
          <ul className="web-todo">
            {c.waiting ? (
              <li>
                <Icon name="chat" size={18} />
                <span>{tn('web.todo_forum', c.waiting)}</span>
                <Button size="sm" variant="ghost" onClick={() => go({ tab: 'forum' })}>
                  {t('web.check')}
                </Button>
              </li>
            ) : null}
            {c.drafts ? (
              <li>
                <Icon name="book" size={18} />
                <span>{tn('web.todo_drafts', c.drafts)}</span>
                <Button size="sm" variant="ghost" onClick={() => go({ tab: 'blog' })}>
                  {t('web.open')}
                </Button>
              </li>
            ) : null}
            {c.pages > c.published ? (
              <li>
                <Icon name="layout" size={18} />
                <span>{tn('web.todo_pages', c.pages - c.published)}</span>
                <Button size="sm" variant="ghost" onClick={() => go({ tab: 'pages' })}>
                  {t('web.open')}
                </Button>
              </li>
            ) : null}
            {!c.waiting && !c.drafts && c.pages === c.published ? (
              <li className="muted">
                <Icon name="check" size={18} />
                <span>{t('web.todo_none')}</span>
              </li>
            ) : null}
          </ul>
        </section>
      </div>
    </>
  );
}

function CopyButton({ text }: { text: string }) {
  const { toast } = useUi();
  return (
    <Button
      variant="ghost"
      icon="copy"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          toast(t('web.copied'));
        } catch {
          window.prompt(t('web.copy_by_hand'), text);
        }
      }}
    >
      {t('web.copy_link')}
    </Button>
  );
}

// ---- pages ---------------------------------------------------------------------------------

function PagesTab({ o, slug, onOpen, onChange }: { o: Overview; slug: string; onOpen: (id: string) => void; onChange: () => void }) {
  const { data: rows, reload } = useAction<PageRow[]>('website.pages');
  const { run } = useSave();
  if (!rows) return <Skeleton h={240} r="var(--radius-lg)" />;
  if (!rows.length) return <OverviewTab o={o} slug={slug} go={() => void reload()} onChange={onChange} />;
  const move = async (i: number, by: -1 | 1) => {
    const order = rows.filter((r) => !r.is_home).map((r) => r.id);
    const at = order.indexOf(rows[i]!.id);
    const to = at + by;
    if (to < 0 || to >= order.length) return;
    [order[at], order[to]] = [order[to]!, order[at]!];
    if (await run(() => action('website.move_page', { order }))) await reload();
  };
  const others = rows.filter((r) => !r.is_home);
  return (
    <section className="card">
      <header className="card-head">
        <div>
          <h2>{t('web.pages')}</h2>
          <span className="muted">{t('web.pages_hint')}</span>
        </div>
        {o.can.edit ? (
          <Button icon="plus" onClick={() => onOpen('new')}>
            {t('web.new_page')}
          </Button>
        ) : null}
      </header>
      <ul className="web-pages">
        {rows.map((p, i) => {
          const pos = others.findIndex((r) => r.id === p.id);
          return (
            <li key={p.id} className="web-page-row">
              <button type="button" className="web-page-open" onClick={() => onOpen(p.id)}>
                <span className={`web-page-icon${p.is_home ? ' home' : ''}`} aria-hidden="true">
                  <Icon name={p.is_home ? 'store' : 'layout'} size={20} />
                </span>
                <span className="web-page-main">
                  <strong>
                    {p.title}
                    {p.is_home ? <span className="web-chip">{t('web.home_page')}</span> : null}
                  </strong>
                  <span className="muted small">
                    /{p.is_home ? '' : p.slug} · {tn('web.n_blocks', p.blocks)} · {t('web.changed', { when: since(p.updated_at) })}
                    {p.updated_by_name ? ` · ${p.updated_by_name}` : ''}
                  </span>
                </span>
              </button>
              <span className="web-page-views hide-phone" title={t('web.views_30_hint')}>
                <strong>{p.views_30.toLocaleString(locale())}</strong>
                <span className="muted small">{t('web.views_30')}</span>
              </span>
              <span className="web-page-state">
                {p.published ? <StatusBadge tone="ok" icon="globe" label={t('web.published')} /> : <StatusBadge tone="neutral" icon="edit" label={t('web.draft')} />}
                {!p.in_menu && !p.is_home ? <span className="muted small hide-phone">{t('web.not_in_menu')}</span> : null}
              </span>
              <span className="web-page-tools">
                {p.published && o.settings.enabled ? (
                  <a
                    className="icon-btn"
                    href={siteUrl(slug, p.is_home ? '' : `/${p.slug}`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('web.open_on_site', { title: p.title })}
                    title={t('web.open_on_site', { title: p.title })}
                  >
                    <Icon name="external" size={18} />
                  </a>
                ) : null}
                {o.can.edit && !p.is_home ? (
                  <>
                    <IconButton icon="up" label={t('web.move_up', { title: p.title })} disabled={pos <= 0} onClick={() => void move(i, -1)} />
                    <IconButton icon="down" label={t('web.move_down', { title: p.title })} disabled={pos >= others.length - 1} onClick={() => void move(i, 1)} />
                  </>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function PageEditor({ id, o, slug, onClose, onSaved, onCreated }: { id: string | null; o: Overview; slug: string; onClose: () => void; onSaved: () => void; onCreated: (id: string) => void }) {
  const [page, setPage] = useState<PageFull | null>(
    id ? null : { id: '', slug: '', title: '', description: null, blocks: [newBlock('hero'), newBlock('text')], published: false, in_menu: true, is_home: false },
  );
  const [dirty, setDirty] = useState(false);
  const [slugTouched, setSlugTouched] = useState(!!id);
  const [adding, setAdding] = useState<number | null>(null);
  const [preview, setPreview] = useState(false);
  const [removing, setRemoving] = useState(false);
  const { run, busy } = useSave();
  const { fail } = useUi();
  const { data: images, reload: reloadImages } = useAction<ImageRow[]>('website.images');
  useEffect(() => {
    if (id) void action<PageFull>('website.page', { id }).then((p) => setPage(keyedPage(p)), fail);
  }, [id, fail]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  if (!page) return <Skeleton h={400} r="var(--radius-lg)" />;
  const can = o.can.edit;
  const patch = (next: Partial<PageFull>) => {
    setPage({ ...page, ...next });
    setDirty(true);
  };
  const setBlock = (i: number, b: EditBlock) => patch({ blocks: page.blocks.map((x, n) => (n === i ? b : x)) });
  const moveBlock = (i: number, by: -1 | 1) => {
    const blocks = [...page.blocks];
    const j = i + by;
    if (j < 0 || j >= blocks.length) return;
    [blocks[i], blocks[j]] = [blocks[j]!, blocks[i]!];
    patch({ blocks });
  };
  const save = async (publish?: boolean) => {
    let out: PageFull | null = null;
    const ok = await run(async () => {
      out = await action<PageFull>('website.save_page', { ...page, id: page.id || null, blocks: page.blocks.map(cleanBlock), published: publish ?? page.published });
    }, t('web.saved'));
    if (ok && out) {
      setPage(keyedPage(out));
      setDirty(false);
      onSaved();
      if (!page.id) onCreated((out as PageFull).id);
    }
  };
  const remove = async () => {
    if (await run(() => action('website.remove_page', { id: page.id }), t('web.page_removed'))) {
      onSaved();
      onClose();
    }
  };
  return (
    <div className="web-editor">
      <div className="web-editor-head">
        <Button variant="ghost" icon="back" onClick={onClose}>
          {t('web.back_to_pages')}
        </Button>
        <div className="web-editor-title">
          <h2>{page.title || t('web.untitled')}</h2>
          <span className="muted small">
            {page.is_home ? t('web.home_page') : `/${page.slug || '…'}`} · {page.published ? t('web.published') : t('web.draft')}
            {dirty ? ` · ${t('web.unsaved')}` : ''}
          </span>
        </div>
        <div className="web-editor-actions">
          <Button variant="ghost" icon="eye" onClick={() => setPreview(true)}>
            {t('web.preview')}
          </Button>
          {can ? (
            <>
              {o.can.publish && !page.published ? (
                <Button variant="ghost" icon="globe" busy={busy} onClick={() => void save(true)}>
                  {t('web.save_publish')}
                </Button>
              ) : null}
              <Button icon="check" busy={busy} disabled={!dirty && !!page.id} onClick={() => void save()}>
                {t('web.save')}
              </Button>
            </>
          ) : null}
        </div>
      </div>
      <div className="web-editor-grid">
        <aside className="card web-editor-side">
          <h3 className="web-h3">{t('web.page_details')}</h3>
          <Field label={t('web.f_title')} required>
            <input
              className="input"
              value={page.title}
              maxLength={120}
              disabled={!can}
              onChange={(e) => patch({ title: e.target.value, ...(slugTouched || page.is_home ? {} : { slug: slugify(e.target.value) }) })}
            />
          </Field>
          <Field label={t('web.f_address')} hint={t('web.f_address_hint', { url: siteUrl(slug, `/${page.slug || '…'}`) })}>
            <input
              className="input mono"
              value={page.slug}
              maxLength={60}
              disabled={!can}
              onChange={(e) => {
                setSlugTouched(true);
                patch({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') });
              }}
            />
          </Field>
          <Field label={t('web.f_description')} hint={t('web.f_description_hint')} counter={`${(page.description ?? '').length}/300`}>
            <textarea className="input" rows={3} maxLength={300} value={page.description ?? ''} disabled={!can} onChange={(e) => patch({ description: e.target.value })} />
          </Field>
          {!page.is_home ? (
            <div className="setting-row">
              <div>
                <strong>{t('web.in_menu')}</strong>
                <p className="muted">{t('web.in_menu_hint')}</p>
              </div>
              <Switch checked={page.in_menu} onChange={(v) => patch({ in_menu: v })} label={t('web.in_menu')} disabled={!can} />
            </div>
          ) : null}
          <div className="setting-row">
            <div>
              <strong>{t('web.on_site')}</strong>
              <p className="muted">{o.can.publish ? t('web.on_site_hint') : t('web.on_site_ask')}</p>
            </div>
            <Switch checked={page.published} onChange={(v) => patch({ published: v })} label={t('web.on_site')} disabled={!o.can.publish || (page.is_home && o.settings.enabled)} />
          </div>
          {page.id && can ? (
            <div className="web-side-actions">
              {!page.is_home && page.published ? (
                <Button
                  variant="ghost"
                  size="sm"
                  icon="store"
                  onClick={() =>
                    void run(() => action('website.set_home', { id: page.id }), t('web.now_home')).then((ok) => {
                      if (ok) {
                        setPage({ ...page, is_home: true });
                        onSaved();
                      }
                    })
                  }
                >
                  {t('web.make_home')}
                </Button>
              ) : null}
              {!page.is_home ? (
                <Button variant="ghost" size="sm" icon="trash" onClick={() => setRemoving(true)}>
                  {t('web.remove_page')}
                </Button>
              ) : null}
            </div>
          ) : null}
          <details className="web-help">
            <summary>{t('web.writing_help')}</summary>
            <p className="muted small">{t('web.writing_help_body')}</p>
          </details>
        </aside>
        <div className="web-blocks">
          {page.blocks.map((b, i) => (
            <BlockCard
              key={b._k ?? i}
              block={b}
              index={i}
              count={page.blocks.length}
              can={can}
              images={images ?? []}
              onImages={reloadImages}
              slug={slug}
              onChange={(nb) => setBlock(i, nb)}
              onMove={(by) => moveBlock(i, by)}
              onRemove={() => patch({ blocks: page.blocks.filter((_, n) => n !== i) })}
              onAddAfter={() => setAdding(i + 1)}
            />
          ))}
          {can ? (
            <button type="button" className="web-add-block" onClick={() => setAdding(page.blocks.length)} disabled={page.blocks.length >= 24}>
              <Icon name="plus" size={20} />
              {t('web.add_block')}
            </button>
          ) : null}
          {!page.blocks.length ? <EmptyState icon="layout" title={t('web.no_blocks')} body={t('web.no_blocks_body')} /> : null}
        </div>
      </div>
      {adding !== null ? (
        <Dialog title={t('web.add_block')} onClose={() => setAdding(null)} size="lg">
          <ul className="web-kinds">
            {BLOCK_KINDS.map((k) => (
              <li key={k}>
                <button
                  type="button"
                  onClick={() => {
                    const blocks = [...page.blocks];
                    blocks.splice(adding, 0, newBlock(k));
                    patch({ blocks });
                    setAdding(null);
                  }}
                >
                  <span className="web-kind-icon" aria-hidden="true">
                    <Icon name={BLOCK_ICON[k]} size={22} />
                  </span>
                  <strong>{t(`web.b_${k}` as MessageKey)}</strong>
                  <span className="muted small">{t(`web.b_${k}_hint` as MessageKey)}</span>
                </button>
              </li>
            ))}
          </ul>
        </Dialog>
      ) : null}
      {preview ? <PreviewDialog blocks={page.blocks} title={page.title} slug={slug} onClose={() => setPreview(false)} /> : null}
      {removing ? (
        <Confirm
          title={t('web.remove_page_q', { title: page.title })}
          body={t('web.remove_page_body')}
          confirm={t('web.remove_page')}
          danger
          busy={busy}
          onConfirm={() => void remove()}
          onClose={() => setRemoving(false)}
        />
      ) : null}
    </div>
  );
}

/** Empty texts go to the server as nothing, so a block with an empty heading shows none. */
function cleanBlock(b: EditBlock): EditBlock {
  const { _k, _new, ...rest } = b;
  void _k;
  void _new;
  const out = rest as EditBlock;
  for (const [k, v] of Object.entries(out)) if (typeof v === 'string' && !v.trim() && k !== 'kind') out[k] = null;
  if (Array.isArray(out.items))
    out.items = out.items.map((it: Record<string, unknown>) => Object.fromEntries(Object.entries(it).map(([k, v]) => [k, typeof v === 'string' && !v.trim() && k === 'text' ? null : v])));
  return out;
}

function PreviewDialog({ blocks, title, slug, onClose }: { blocks: EditBlock[]; title: string; slug: string; onClose: () => void }) {
  const [data, setData] = useState<{ blocks: ShownBlock[]; site: SiteChrome } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [width, setWidth] = useState<'desk' | 'phone'>('desk');
  useEffect(() => {
    action<{ blocks: ShownBlock[]; site: SiteChrome }>('website.preview', { blocks: blocks.map(cleanBlock) }).then(setData, (e) => setError((e as Error).message));
  }, [blocks]);
  const env: SiteEnv | null = data ? { tenant: slug, site: data.site, go: () => {}, preview: true, contactForm: () => <PreviewForm /> } : null;
  return (
    <Dialog title={t('web.preview_of', { title: title || t('web.untitled') })} onClose={onClose} size="xl">
      <div className="segmented web-preview-switch" role="radiogroup" aria-label={t('web.preview_width')}>
        {(['desk', 'phone'] as const).map((w) => (
          <button key={w} role="radio" aria-checked={width === w} className={width === w ? 'active' : ''} onClick={() => setWidth(w)}>
            <Icon name={w === 'desk' ? 'layout' : 'phone'} size={16} />
            {t(`web.preview_${w}` as MessageKey)}
          </button>
        ))}
      </div>
      {error ? (
        <p className="callout callout-danger">
          <Icon name="alert" size={18} /> {error}
        </p>
      ) : !env ? (
        <Skeleton h={300} />
      ) : (
        <div className={`web-preview ${width}`}>
          <div className={`site-page accent-${env.site.accent}`}>
            <Blocks env={env} blocks={data!.blocks} />
          </div>
        </div>
      )}
    </Dialog>
  );
}

function PreviewForm() {
  return (
    <div className="web-preview-form">
      <h3 className="site-form-title">{t('site.send_message')}</h3>
      <p className="muted">{t('web.preview_form')}</p>
    </div>
  );
}

// ---- one block -----------------------------------------------------------------------------

function BlockCard({
  block,
  index,
  count,
  can,
  images,
  onImages,
  slug,
  onChange,
  onMove,
  onRemove,
  onAddAfter,
}: {
  block: EditBlock;
  index: number;
  count: number;
  can: boolean;
  images: ImageRow[];
  onImages: () => void;
  slug: string;
  onChange: (b: EditBlock) => void;
  onMove: (by: -1 | 1) => void;
  onRemove: () => void;
  onAddAfter: () => void;
}) {
  const [open, setOpen] = useState(block._new === true);
  const set = (k: string, v: unknown) => onChange({ ...block, [k]: v });
  const items = (block.items ?? []) as Record<string, any>[];
  const setItem = (i: number, k: string, v: unknown) =>
    set(
      'items',
      items.map((it, n) => (n === i ? { ...it, [k]: v } : it)),
    );
  const moveItem = (i: number, by: -1 | 1) => {
    const next = [...items];
    const j = i + by;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j]!, next[i]!];
    set('items', next);
  };
  const kindLabel = t(`web.b_${block.kind}` as MessageKey);
  const summary = (block.heading as string | null) || kindLabel;
  const text = (k: string, label: MessageKey, max: number, rows?: number, hint?: string) => (
    <Field label={t(label)} hint={hint} counter={rows ? `${String(block[k] ?? '').length}/${max}` : undefined}>
      {rows ? (
        <textarea className="input" rows={rows} maxLength={max} value={(block[k] as string | null) ?? ''} disabled={!can} onChange={(e) => set(k, e.target.value)} />
      ) : (
        <input className="input" maxLength={max} value={(block[k] as string | null) ?? ''} disabled={!can} onChange={(e) => set(k, e.target.value)} />
      )}
    </Field>
  );
  const button = () => (
    <div className="form-grid">
      {text('button_label', 'web.f_button', 40)}
      <Field label={t('web.f_button_link')} hint={t('web.f_button_link_hint')}>
        <input
          className="input mono"
          maxLength={300}
          value={(block.button_link as string | null) ?? ''}
          disabled={!can}
          onChange={(e) => set('button_link', e.target.value.trim())}
          placeholder="contact"
        />
      </Field>
    </div>
  );
  return (
    <section className={`card web-block${open ? ' open' : ''}`} aria-label={t('web.block_n', { n: index + 1, kind: kindLabel })}>
      <header className="web-block-head">
        <button type="button" className="web-block-toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
          <span className="web-kind-icon small" aria-hidden="true">
            <Icon name={BLOCK_ICON[block.kind]} size={18} />
          </span>
          <span className="web-block-name">
            <strong>{summary}</strong>
            <span className="muted small">{kindLabel}</span>
          </span>
          <Icon name={open ? 'up' : 'down'} size={18} />
        </button>
        {can ? (
          <span className="web-block-tools">
            <IconButton icon="up" label={t('web.block_up')} disabled={index === 0} onClick={() => onMove(-1)} />
            <IconButton icon="down" label={t('web.block_down')} disabled={index === count - 1} onClick={() => onMove(1)} />
            <IconButton icon="plus" label={t('web.add_below')} onClick={onAddAfter} />
            <IconButton icon="trash" label={t('web.remove_block')} onClick={onRemove} />
          </span>
        ) : null}
      </header>
      {open ? (
        <div className="web-block-body">
          {text('heading', 'web.f_heading', 160)}
          {block.kind === 'hero' ? (
            <>
              {text('text', 'web.f_text', 600, 3)}
              {button()}
              <ImagePicker value={block.image} images={images} onImages={onImages} slug={slug} can={can} onChange={(v) => set('image', v)} />
            </>
          ) : block.kind === 'text' ? (
            text('text', 'web.f_body', 6000, 8, t('web.f_body_hint'))
          ) : block.kind === 'image_text' ? (
            <>
              {text('text', 'web.f_body', 6000, 6, t('web.f_body_hint'))}
              <div className="segmented" role="radiogroup" aria-label={t('web.f_side')}>
                {(['left', 'right'] as const).map((s) => (
                  <button key={s} role="radio" aria-checked={block.side === s} className={block.side === s ? 'active' : ''} disabled={!can} onClick={() => set('side', s)}>
                    {t(`web.side_${s}` as MessageKey)}
                  </button>
                ))}
              </div>
              <ImagePicker value={block.image} images={images} onImages={onImages} slug={slug} can={can} onChange={(v) => set('image', v)} />
              {button()}
            </>
          ) : block.kind === 'cta' ? (
            <>
              {text('text', 'web.f_text', 400, 2)}
              {button()}
            </>
          ) : block.kind === 'contact' ? (
            <>
              {text('text', 'web.f_text', 600, 3)}
              <p className="muted small">{t('web.contact_block_hint')}</p>
            </>
          ) : block.kind === 'posts' ? (
            <Field label={t('web.f_count')}>
              <select className="input" value={block.count} disabled={!can} onChange={(e) => set('count', Number(e.target.value))}>
                <option value={3}>{tn('web.latest_n', 3)}</option>
                <option value={6}>{tn('web.latest_n', 6)}</option>
              </select>
            </Field>
          ) : block.kind === 'gallery' ? (
            <GalleryPicker value={block.images as string[]} images={images} onImages={onImages} slug={slug} can={can} onChange={(v) => set('images', v)} />
          ) : (
            <div className="web-items">
              {items.map((it, i) => (
                <div key={i} className="web-item">
                  <div className="web-item-head">
                    <span className="muted small">{t('web.item_n', { n: i + 1 })}</span>
                    {can ? (
                      <span className="web-block-tools">
                        <IconButton icon="up" label={t('web.item_up')} disabled={i === 0} onClick={() => moveItem(i, -1)} />
                        <IconButton icon="down" label={t('web.item_down')} disabled={i === items.length - 1} onClick={() => moveItem(i, 1)} />
                        <IconButton
                          icon="trash"
                          label={t('web.item_remove')}
                          onClick={() =>
                            set(
                              'items',
                              items.filter((_, n) => n !== i),
                            )
                          }
                        />
                      </span>
                    ) : null}
                  </div>
                  {block.kind === 'features' ? (
                    <>
                      <div className="web-icons" role="radiogroup" aria-label={t('web.f_icon')}>
                        {FEATURE_ICONS.map((ic) => (
                          <button
                            key={ic}
                            type="button"
                            role="radio"
                            aria-checked={it.icon === ic}
                            aria-label={ic}
                            className={it.icon === ic ? 'active' : ''}
                            disabled={!can}
                            onClick={() => setItem(i, 'icon', ic)}
                          >
                            <Icon name={ic} size={18} />
                          </button>
                        ))}
                      </div>
                      <Field label={t('web.f_feature_title')} required>
                        <input className="input" maxLength={80} value={it.title ?? ''} disabled={!can} onChange={(e) => setItem(i, 'title', e.target.value)} />
                      </Field>
                      <Field label={t('web.f_feature_text')}>
                        <textarea className="input" rows={2} maxLength={400} value={it.text ?? ''} disabled={!can} onChange={(e) => setItem(i, 'text', e.target.value)} />
                      </Field>
                    </>
                  ) : block.kind === 'faq' ? (
                    <>
                      <Field label={t('web.f_question')} required>
                        <input className="input" maxLength={200} value={it.q ?? ''} disabled={!can} onChange={(e) => setItem(i, 'q', e.target.value)} />
                      </Field>
                      <Field label={t('web.f_answer')} required>
                        <textarea className="input" rows={3} maxLength={2000} value={it.a ?? ''} disabled={!can} onChange={(e) => setItem(i, 'a', e.target.value)} />
                      </Field>
                    </>
                  ) : (
                    <>
                      <Field label={t('web.f_quote')} required>
                        <textarea className="input" rows={3} maxLength={500} value={it.quote ?? ''} disabled={!can} onChange={(e) => setItem(i, 'quote', e.target.value)} />
                      </Field>
                      <Field label={t('web.f_who')} required>
                        <input className="input" maxLength={80} value={it.name ?? ''} disabled={!can} onChange={(e) => setItem(i, 'name', e.target.value)} />
                      </Field>
                    </>
                  )}
                </div>
              ))}
              {can && items.length < (block.kind === 'faq' ? 20 : 6) ? (
                <Button
                  variant="ghost"
                  size="sm"
                  icon="plus"
                  onClick={() => set('items', [...items, block.kind === 'features' ? { icon: 'star', title: '', text: '' } : block.kind === 'faq' ? { q: '', a: '' } : { quote: '', name: '' }])}
                >
                  {t(`web.add_${block.kind}` as MessageKey)}
                </Button>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}

// ---- pictures ------------------------------------------------------------------------------

function useUpload(onDone: (img: ImageRow) => void) {
  const [busy, setBusy] = useState(false);
  const { fail, toast } = useUi();
  const upload = async (file: File) => {
    setBusy(true);
    try {
      let shrunk = await shrinkPhoto(file, 1600, 0.82);
      if (shrunk.base64.length * 0.75 > MAX_IMAGE) shrunk = await shrinkPhoto(file, 1200, 0.72);
      const size = await new Promise<{ w: number; h: number }>((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = () => resolve({ w: 0, h: 0 });
        img.src = shrunk.url;
      });
      const out = await action<ImageRow>('website.upload_image', { name: file.name.slice(0, 120) || 'Picture', data: shrunk.base64, width: size.w || null, height: size.h || null });
      toast(t('web.picture_added'));
      onDone({ ...out, used: 0, created_at: new Date().toISOString() });
    } catch (err) {
      fail(err instanceof Error && err.message === 'not_image' ? new Error(t('web.not_a_picture')) : err);
    } finally {
      setBusy(false);
    }
  };
  return { busy, upload };
}

function Library({ images, slug, onPick, onImages, picked }: { images: ImageRow[]; slug: string; onPick: (img: ImageRow) => void; onImages: () => void; picked?: string[] }) {
  const { busy, upload } = useUpload((img) => {
    onImages();
    onPick(img);
  });
  const [removing, setRemoving] = useState<ImageRow | null>(null);
  const { run } = useSave();
  return (
    <div className="web-library">
      <label className={`web-upload${busy ? ' busy' : ''}`}>
        <Icon name="upload" size={22} />
        <strong>{busy ? t('web.uploading') : t('web.upload')}</strong>
        <span className="muted small">{t('web.upload_hint')}</span>
        <input type="file" accept="image/jpeg,image/png,image/webp" disabled={busy} onChange={(e) => e.target.files?.[0] && void upload(e.target.files[0])} />
      </label>
      {images.length ? (
        <ul className="web-thumbs">
          {images.map((img) => (
            <li key={img.id} className={picked?.includes(img.id) ? 'picked' : undefined}>
              <button type="button" onClick={() => onPick(img)} aria-label={t('web.choose_named', { name: img.name })} aria-pressed={picked?.includes(img.id)}>
                <img src={imageUrl(slug, img)} alt="" loading="lazy" />
                {picked?.includes(img.id) ? (
                  <span className="web-thumb-check" aria-hidden="true">
                    <Icon name="check" size={16} />
                  </span>
                ) : null}
              </button>
              <span className="web-thumb-foot">
                <span className="small web-thumb-name" title={img.name}>
                  {img.name}
                </span>
                {!img.used ? <IconButton icon="trash" label={t('web.remove_picture', { name: img.name })} onClick={() => setRemoving(img)} /> : null}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">{t('web.no_pictures')}</p>
      )}
      {removing ? (
        <Confirm
          title={t('web.remove_picture', { name: removing.name })}
          body={t('web.remove_picture_body')}
          confirm={t('web.remove')}
          danger
          onConfirm={() =>
            void run(() => action('website.remove_image', { id: removing.id }), t('web.picture_removed')).then(() => {
              setRemoving(null);
              onImages();
            })
          }
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </div>
  );
}

function ImagePicker({
  value,
  images,
  onImages,
  slug,
  can,
  onChange,
}: {
  value: string | null;
  images: ImageRow[];
  onImages: () => void;
  slug: string;
  can: boolean;
  onChange: (id: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const img = images.find((i) => i.id === value);
  return (
    <div className="web-picker">
      <span className="field-label">{t('web.f_picture')}</span>
      <div className="web-picker-row">
        {img ? (
          <img className="web-picker-img" src={imageUrl(slug, img)} alt="" />
        ) : (
          <span className="web-picker-img empty" aria-hidden="true">
            <Icon name="image" size={24} />
          </span>
        )}
        {can ? (
          <span className="web-picker-actions">
            <Button size="sm" variant="ghost" icon="image" onClick={() => setOpen(true)}>
              {img ? t('web.change_picture') : t('web.choose_picture')}
            </Button>
            {img ? (
              <Button size="sm" variant="ghost" icon="x" onClick={() => onChange(null)}>
                {t('web.no_picture')}
              </Button>
            ) : null}
          </span>
        ) : null}
      </div>
      {open ? (
        <Dialog title={t('web.choose_picture')} onClose={() => setOpen(false)} size="lg">
          <Library
            images={images}
            slug={slug}
            onImages={onImages}
            picked={value ? [value] : []}
            onPick={(i) => {
              onChange(i.id);
              setOpen(false);
            }}
          />
        </Dialog>
      ) : null}
    </div>
  );
}

function GalleryPicker({ value, images, onImages, slug, can, onChange }: { value: string[]; images: ImageRow[]; onImages: () => void; slug: string; can: boolean; onChange: (ids: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const byId = new Map(images.map((i) => [i.id, i]));
  return (
    <div className="web-picker">
      <span className="field-label">{tn('web.n_pictures', value.length)}</span>
      <ul className="web-gallery-edit">
        {value.map((id, i) =>
          byId.get(id) ? (
            <li key={id}>
              <img src={imageUrl(slug, byId.get(id)!)} alt="" />
              {can ? (
                <span className="web-gallery-tools">
                  <IconButton icon="up" label={t('web.item_up')} disabled={i === 0} onClick={() => onChange(value.map((x, n) => (n === i - 1 ? id : n === i ? value[i - 1]! : x)))} />
                  <IconButton icon="x" label={t('web.item_remove')} onClick={() => onChange(value.filter((x) => x !== id))} />
                </span>
              ) : null}
            </li>
          ) : null,
        )}
      </ul>
      {can && value.length < 12 ? (
        <Button size="sm" variant="ghost" icon="plus" onClick={() => setOpen(true)}>
          {t('web.add_pictures')}
        </Button>
      ) : null}
      {open ? (
        <Dialog
          title={t('web.add_pictures')}
          onClose={() => setOpen(false)}
          size="lg"
          footer={
            <Button icon="check" onClick={() => setOpen(false)}>
              {t('web.done')}
            </Button>
          }
        >
          <Library
            images={images}
            slug={slug}
            onImages={onImages}
            picked={value}
            onPick={(i) => onChange(value.includes(i.id) ? value.filter((x) => x !== i.id) : value.length < 12 ? [...value, i.id] : value)}
          />
        </Dialog>
      ) : null}
    </div>
  );
}

// ---- blog ----------------------------------------------------------------------------------

function BlogTab({ o, slug, onOpen }: { o: Overview; slug: string; onOpen: (id: string) => void }) {
  const [state, setState] = useState<'' | 'draft' | 'published'>('');
  const { data: rows } = useAction<PostRow[]>('website.posts', { state: state || null });
  return (
    <section className="card">
      <header className="card-head">
        <div>
          <h2>{t('web.blog')}</h2>
          <span className="muted">{o.settings.blog ? t('web.blog_hint') : t('web.blog_off')}</span>
        </div>
        <div className="web-head-tools">
          <div className="segmented" role="radiogroup" aria-label={t('web.show')}>
            {(['', 'published', 'draft'] as const).map((s) => (
              <button key={s} role="radio" aria-checked={state === s} className={state === s ? 'active' : ''} onClick={() => setState(s)}>
                {t(`web.posts_${s || 'all'}` as MessageKey)}
              </button>
            ))}
          </div>
          {o.can.edit ? (
            <Button icon="plus" onClick={() => onOpen('new')}>
              {t('web.new_post')}
            </Button>
          ) : null}
        </div>
      </header>
      {!rows ? (
        <Skeleton h={200} />
      ) : !rows.length ? (
        <EmptyState icon="book" title={t('web.no_posts')} body={t('web.no_posts_body')} />
      ) : (
        <ul className="web-posts">
          {rows.map((p) => (
            <li key={p.id}>
              <button type="button" className="web-post-row" onClick={() => onOpen(p.id)}>
                {p.cover && p.cover_version ? (
                  <img className="web-post-thumb" src={imageUrl(slug, { id: p.cover, version: p.cover_version })} alt="" />
                ) : (
                  <span className={`web-post-thumb empty tone-${o.settings.accent}`} aria-hidden="true">
                    <Icon name="book" size={22} />
                  </span>
                )}
                <span className="web-post-main">
                  <strong>{p.title}</strong>
                  <span className="muted small">
                    {p.state === 'published' && p.published_at ? day(p.published_at.slice(0, 10)) : t('web.changed', { when: since(p.updated_at) })}
                    {p.author_name ? ` · ${p.author_name}` : ''}
                    {p.tags.length ? ` · ${p.tags.map((x) => `#${x}`).join(' ')}` : ''}
                  </span>
                  {p.summary ? <span className="muted small web-clamp">{p.summary}</span> : null}
                </span>
                <span className="web-post-side">
                  {p.state === 'published' ? <StatusBadge tone="ok" icon="globe" label={t('web.published')} /> : <StatusBadge tone="neutral" icon="edit" label={t('web.draft')} />}
                  {p.state === 'published' ? <span className="muted small">{tn('web.n_views', p.views)}</span> : null}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function PostEditor({ id, o, slug, onClose, onSaved, onCreated }: { id: string | null; o: Overview; slug: string; onClose: () => void; onSaved: () => void; onCreated: (id: string) => void }) {
  const [post, setPost] = useState<PostFull | null>(
    id ? null : { id: '', slug: '', title: '', summary: null, body: '', cover: null, cover_version: null, tags: [], state: 'draft', published_at: null },
  );
  const [tags, setTags] = useState('');
  const [dirty, setDirty] = useState(false);
  const [slugTouched, setSlugTouched] = useState(!!id);
  const [removing, setRemoving] = useState(false);
  const [preview, setPreview] = useState(false);
  const { run, busy } = useSave();
  const { fail } = useUi();
  const { data: images, reload: reloadImages } = useAction<ImageRow[]>('website.images');
  useEffect(() => {
    if (id)
      void action<PostFull>('website.post', { id }).then((p) => {
        setPost(p);
        setTags(p.tags.join(', '));
      }, fail);
  }, [id, fail]);
  if (!post) return <Skeleton h={400} r="var(--radius-lg)" />;
  const can = o.can.edit;
  const patch = (next: Partial<PostFull>) => {
    setPost({ ...post, ...next });
    setDirty(true);
  };
  const tagList = tags
    .split(',')
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean);
  const save = async () => {
    let out: PostFull | null = null;
    const ok = await run(async () => {
      out = await action<PostFull>('website.save_post', { id: post.id || null, slug: post.slug, title: post.title, summary: post.summary, body: post.body, cover: post.cover, tags: tagList });
    }, t('web.saved'));
    if (ok && out) {
      setPost({ ...post, ...(out as PostFull) });
      setDirty(false);
      onSaved();
      if (!post.id) onCreated((out as PostFull).id);
    }
    return ok;
  };
  const publish = async (on: boolean) => {
    if (dirty && !(await save())) return;
    let out: PostFull | null = null;
    if (
      await run(
        async () => {
          out = await action<PostFull>('website.publish_post', { id: post.id, publish: on });
        },
        on ? t('web.post_published') : t('web.post_unpublished'),
      )
    ) {
      setPost({ ...post, ...(out as unknown as PostFull) });
      onSaved();
    }
  };
  const cover = images?.find((i) => i.id === post.cover);
  return (
    <div className="web-editor">
      <div className="web-editor-head">
        <Button variant="ghost" icon="back" onClick={onClose}>
          {t('web.back_to_blog')}
        </Button>
        <div className="web-editor-title">
          <h2>{post.title || t('web.untitled')}</h2>
          <span className="muted small">
            {post.state === 'published' ? t('web.published') : t('web.draft')}
            {dirty ? ` · ${t('web.unsaved')}` : ''}
          </span>
        </div>
        <div className="web-editor-actions">
          <Button variant="ghost" icon="eye" onClick={() => setPreview(true)} disabled={!post.body.trim()}>
            {t('web.preview')}
          </Button>
          {post.state === 'published' && o.settings.enabled && o.settings.blog ? (
            <a className="btn btn-ghost" href={siteUrl(slug, `/blog/${post.slug}`)} target="_blank" rel="noopener noreferrer">
              <Icon name="external" size={18} />
              <span className="btn-label">{t('web.open_post')}</span>
            </a>
          ) : null}
          {can ? (
            <>
              {o.can.publish && post.id ? (
                post.state === 'published' ? (
                  <Button variant="ghost" icon="pause" busy={busy} onClick={() => void publish(false)}>
                    {t('web.unpublish')}
                  </Button>
                ) : (
                  <Button variant="ghost" icon="globe" busy={busy} onClick={() => void publish(true)}>
                    {t('web.publish')}
                  </Button>
                )
              ) : null}
              <Button icon="check" busy={busy} disabled={!dirty && !!post.id} onClick={() => void save()}>
                {t('web.save')}
              </Button>
            </>
          ) : null}
        </div>
      </div>
      <div className="web-editor-grid">
        <aside className="card web-editor-side">
          <h3 className="web-h3">{t('web.post_details')}</h3>
          <Field label={t('web.f_address')} hint={t('web.f_address_hint', { url: siteUrl(slug, `/blog/${post.slug || '…'}`) })}>
            <input
              className="input mono"
              value={post.slug}
              maxLength={60}
              disabled={!can}
              onChange={(e) => {
                setSlugTouched(true);
                patch({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') });
              }}
            />
          </Field>
          <Field label={t('web.f_tags')} hint={t('web.f_tags_hint')}>
            <input
              className="input"
              value={tags}
              maxLength={240}
              disabled={!can}
              onChange={(e) => {
                setTags(e.target.value);
                setDirty(true);
              }}
            />
          </Field>
          <ImagePicker value={post.cover} images={images ?? []} onImages={reloadImages} slug={slug} can={can} onChange={(v) => patch({ cover: v })} />
          {cover ? null : <p className="muted small">{t('web.cover_hint')}</p>}
          {post.id && can && post.state === 'draft' ? (
            <div className="web-side-actions">
              <Button variant="ghost" size="sm" icon="trash" onClick={() => setRemoving(true)}>
                {t('web.remove_post')}
              </Button>
            </div>
          ) : null}
          {!o.can.publish ? <p className="callout callout-info small">{t('web.publish_ask')}</p> : null}
        </aside>
        <div className="card web-post-form">
          <Field label={t('web.f_title')} required>
            <input
              className="input web-title-input"
              value={post.title}
              maxLength={160}
              disabled={!can}
              onChange={(e) => patch({ title: e.target.value, ...(slugTouched ? {} : { slug: slugify(e.target.value) }) })}
            />
          </Field>
          <Field label={t('web.f_summary')} hint={t('web.f_summary_hint')} counter={`${(post.summary ?? '').length}/400`}>
            <textarea className="input" rows={2} maxLength={400} value={post.summary ?? ''} disabled={!can} onChange={(e) => patch({ summary: e.target.value })} />
          </Field>
          <Field label={t('web.f_post_body')} required hint={t('web.f_body_hint')} counter={`${post.body.length}/30000`}>
            <textarea className="input web-body-input" rows={18} maxLength={30000} value={post.body} disabled={!can} onChange={(e) => patch({ body: e.target.value })} />
          </Field>
        </div>
      </div>
      {preview ? (
        <Dialog title={t('web.preview_of', { title: post.title || t('web.untitled') })} onClose={() => setPreview(false)} size="xl">
          <div className="web-preview desk">
            <div className={`site-page accent-${o.settings.accent}`}>
              <article className="site-article">
                <div className="site-in site-narrow">
                  <h1 className="site-article-title">{post.title}</h1>
                  {post.summary ? <p className="site-lead">{post.summary}</p> : null}
                </div>
                {cover ? (
                  <div className="site-in site-article-cover">
                    <img src={imageUrl(slug, cover)} alt="" />
                  </div>
                ) : null}
                <div className="site-in site-narrow">
                  <RichText text={post.body} className="site-article-body" />
                </div>
              </article>
            </div>
          </div>
        </Dialog>
      ) : null}
      {removing ? (
        <Confirm
          title={t('web.remove_post_q', { title: post.title })}
          body={t('web.remove_post_body')}
          confirm={t('web.remove_post')}
          danger
          busy={busy}
          onConfirm={() =>
            void run(() => action('website.remove_post', { id: post.id }), t('web.post_removed')).then((ok) => {
              if (ok) {
                onSaved();
                onClose();
              }
            })
          }
          onClose={() => setRemoving(false)}
        />
      ) : null}
    </div>
  );
}

// ---- forum ---------------------------------------------------------------------------------

const TOPIC_TONE: Record<TopicRow['state'], Tone> = { pending: 'warn', open: 'ok', closed: 'neutral', hidden: 'danger' };
const TOPIC_ICON: Record<TopicRow['state'], IconName> = { pending: 'clock', open: 'chat', closed: 'lock', hidden: 'eye' };

function ForumTab({ o, onOpen, onChange }: { o: Overview; onOpen: (id: string) => void; onChange: () => void }) {
  const [state, setState] = useState<'' | 'waiting' | 'open' | 'closed' | 'hidden'>(o.counts.waiting ? 'waiting' : '');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const { data: rows, reload } = useAction<TopicRow[]>('website.topics', { state: state || null, q: search || null });
  const [starting, setStarting] = useState(false);
  return (
    <section className="card">
      <header className="card-head">
        <div>
          <h2>{t('web.forum')}</h2>
          <span className="muted">{!o.settings.forum ? t('web.forum_off') : o.settings.moderate ? t('web.forum_moderated') : t('web.forum_open')}</span>
        </div>
        {o.can.moderate ? (
          <Button icon="plus" onClick={() => setStarting(true)}>
            {t('web.new_topic')}
          </Button>
        ) : null}
      </header>
      <div className="web-filters">
        <div className="segmented" role="radiogroup" aria-label={t('web.show')}>
          {(['waiting', '', 'open', 'closed', 'hidden'] as const).map((s) => (
            <button key={s} role="radio" aria-checked={state === s} className={state === s ? 'active' : ''} onClick={() => setState(s)}>
              {t(`web.topics_${s || 'all'}` as MessageKey)}
              {s === 'waiting' && o.counts.waiting ? <span className="count">{o.counts.waiting}</span> : null}
            </button>
          ))}
        </div>
        <form
          className="web-search"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(q.trim());
          }}
        >
          <Icon name="search" size={18} />
          <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('web.search_topics')} aria-label={t('web.search_topics')} />
        </form>
      </div>
      {!rows ? (
        <Skeleton h={200} />
      ) : !rows.length ? (
        <EmptyState icon="chat" title={state === 'waiting' ? t('web.nothing_waiting') : t('web.no_topics')} body={state === 'waiting' ? t('web.nothing_waiting_body') : undefined} />
      ) : (
        <ul className="web-topics">
          {rows.map((r) => (
            <li key={r.id}>
              <button type="button" className="web-topic-row" onClick={() => onOpen(r.id)}>
                <span className="web-topic-main">
                  <strong>
                    {r.pinned ? <Icon name="pin" size={14} /> : null} {r.title}
                  </strong>
                  <span className="muted small">
                    #{r.number} · {r.name}
                    {r.staff_name ? ` (${t('web.team')})` : ''} · {t('web.active', { when: since(r.last_at) })}
                  </span>
                </span>
                <span className="web-topic-side">
                  <StatusBadge tone={TOPIC_TONE[r.state]} icon={TOPIC_ICON[r.state]} label={t(`web.t_${r.state}` as MessageKey)} />
                  {r.solved ? <StatusBadge tone="ok" icon="check" label={t('web.answered')} /> : null}
                  {r.waiting ? <span className="web-chip warn">{tn('web.replies_waiting', r.waiting)}</span> : null}
                  <span className="muted small hide-phone">{tn('web.n_replies', r.replies)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {starting ? (
        <NewTopicDialog
          onClose={() => setStarting(false)}
          onDone={(id) => {
            setStarting(false);
            onChange();
            void reload();
            onOpen(id);
          }}
        />
      ) : null}
    </section>
  );
}

function NewTopicDialog({ onClose, onDone }: { onClose: () => void; onDone: (id: string) => void }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [pinned, setPinned] = useState(false);
  const { run, busy } = useSave();
  const submit = async () => {
    let id = '';
    if (await run(async () => (id = (await action<{ id: string }>('website.start_topic', { title, body, pinned })).id), t('web.topic_started'))) onDone(id);
  };
  return (
    <Dialog
      title={t('web.new_topic')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button icon="send" busy={busy} onClick={() => void submit()} disabled={!title.trim() || !body.trim()}>
            {t('web.post_topic')}
          </Button>
        </>
      }
    >
      <p className="muted">{t('web.new_topic_hint')}</p>
      <Field label={t('web.f_title')} required>
        <input className="input" value={title} maxLength={160} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <Field label={t('web.f_post_body')} required>
        <textarea className="input" rows={6} value={body} maxLength={5000} onChange={(e) => setBody(e.target.value)} />
      </Field>
      <div className="setting-row">
        <div>
          <strong>{t('web.pin')}</strong>
          <p className="muted">{t('web.pin_hint')}</p>
        </div>
        <Switch checked={pinned} onChange={setPinned} label={t('web.pin')} />
      </div>
    </Dialog>
  );
}

function TopicDetail({ id, slug, enabled, onClose, onChange }: { id: string; slug: string; enabled: boolean; onClose: () => void; onChange: () => void }) {
  const { data: tp, reload } = useAction<TopicFull>('website.topic', { id });
  const [reply, setReply] = useState('');
  const [asAnswer, setAsAnswer] = useState(true);
  const { run, busy } = useSave();
  if (!tp) return <Skeleton h={400} r="var(--radius-lg)" />;
  const after = async (ok: boolean) => {
    if (ok) {
      await reload();
      onChange();
    }
  };
  const topicState = (state: 'open' | 'closed' | 'hidden', pinned?: boolean, done?: string) =>
    void run(() => action('website.moderate_topic', { id: tp.id, state, ...(pinned === undefined ? {} : { pinned }) }), done).then(after);
  const replyState = (rid: string, input: Record<string, unknown>, done: string) => void run(() => action('website.moderate_reply', { id: rid, ...input }), done).then(after);
  const send = async () => {
    const ok = await run(() => action('website.reply', { topic: tp.id, body: reply, answer: asAnswer }), t('web.reply_posted'));
    if (ok) setReply('');
    await after(ok);
  };
  const mod = tp.can_moderate;
  return (
    <div className="web-editor">
      <div className="web-editor-head">
        <Button variant="ghost" icon="back" onClick={onClose}>
          {t('web.back_to_forum')}
        </Button>
        <div className="web-editor-title">
          <h2>{tp.title}</h2>
          <span className="muted small">
            #{tp.number} · {tn('web.n_views', tp.views)} · {tn('web.n_replies', tp.replies)}
          </span>
        </div>
        <div className="web-editor-actions">
          {(tp.state === 'open' || tp.state === 'closed') && enabled ? (
            <a className="btn btn-ghost" href={siteUrl(slug, `/forum/${tp.number}`)} target="_blank" rel="noopener noreferrer">
              <Icon name="external" size={18} />
              <span className="btn-label">{t('web.open_topic')}</span>
            </a>
          ) : null}
          {mod ? (
            <>
              {tp.state === 'pending' || tp.state === 'hidden' ? (
                <Button icon="check" busy={busy} onClick={() => topicState('open', undefined, t('web.let_through_done'))}>
                  {tp.state === 'pending' ? t('web.let_through') : t('web.show_again')}
                </Button>
              ) : null}
              {tp.state === 'open' ? (
                <Button variant="ghost" icon="lock" busy={busy} onClick={() => topicState('closed', undefined, t('web.closed_done'))}>
                  {t('web.close_topic')}
                </Button>
              ) : null}
              {tp.state === 'closed' ? (
                <Button variant="ghost" icon="undo" busy={busy} onClick={() => topicState('open', undefined, t('web.reopened'))}>
                  {t('web.reopen')}
                </Button>
              ) : null}
              {tp.state !== 'hidden' ? (
                <Button variant="ghost" icon="eye" busy={busy} onClick={() => topicState('hidden', undefined, t('web.hidden_done'))}>
                  {tp.state === 'pending' ? t('web.reject') : t('web.hide')}
                </Button>
              ) : null}
              {tp.state === 'open' || tp.state === 'closed' ? (
                <Button variant="ghost" icon="pin" busy={busy} onClick={() => topicState(tp.state as 'open' | 'closed', !tp.pinned, tp.pinned ? t('web.unpinned') : t('web.pinned'))}>
                  {tp.pinned ? t('web.unpin') : t('web.pin')}
                </Button>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
      <div className="web-thread">
        <article className={`card web-post web-question${tp.state === 'pending' ? ' waiting' : ''}`}>
          <header className="web-post-head">
            <span className="web-avatar" aria-hidden="true">
              {tp.name.charAt(0).toUpperCase()}
            </span>
            <span>
              <strong>{tp.name}</strong>
              {tp.staff_name ? <span className="web-chip">{t('web.team')}</span> : null}
              <span className="muted small">
                {' '}
                · {dateTime(tp.created_at)}
                {tp.email ? ` · ${tp.email}` : ''}
              </span>
            </span>
            <StatusBadge tone={TOPIC_TONE[tp.state]} icon={TOPIC_ICON[tp.state]} label={t(`web.t_${tp.state}` as MessageKey)} />
          </header>
          <RichText text={tp.body} />
        </article>
        {tp.replies_list.map((r) => (
          <article key={r.id} className={`card web-post${r.answer ? ' answer' : ''}${r.state === 'pending' ? ' waiting' : ''}${r.state === 'hidden' ? ' hidden' : ''}`}>
            <header className="web-post-head">
              <span className="web-avatar" aria-hidden="true">
                {r.name.charAt(0).toUpperCase()}
              </span>
              <span>
                <strong>{r.name}</strong>
                {r.staff ? <span className="web-chip">{t('web.team')}</span> : null}
                <span className="muted small">
                  {' '}
                  · {dateTime(r.created_at)}
                  {r.email ? ` · ${r.email}` : ''}
                </span>
              </span>
              {r.answer ? (
                <StatusBadge tone="ok" icon="check" label={t('web.the_answer')} />
              ) : r.state !== 'visible' ? (
                <StatusBadge tone={r.state === 'pending' ? 'warn' : 'danger'} icon={r.state === 'pending' ? 'clock' : 'eye'} label={t(`web.r_${r.state}` as MessageKey)} />
              ) : null}
            </header>
            <RichText text={r.body} />
            {mod ? (
              <footer className="web-post-tools">
                {r.state !== 'visible' ? (
                  <Button size="sm" icon="check" onClick={() => replyState(r.id, { state: 'visible' }, t('web.let_through_done'))}>
                    {r.state === 'pending' ? t('web.let_through') : t('web.show_again')}
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" icon="check" onClick={() => replyState(r.id, { answer: !r.answer }, r.answer ? t('web.answer_unmarked') : t('web.answer_marked'))}>
                    {r.answer ? t('web.unmark_answer') : t('web.mark_answer')}
                  </Button>
                )}
                {r.state !== 'hidden' ? (
                  <Button size="sm" variant="ghost" icon="eye" onClick={() => replyState(r.id, { state: 'hidden' }, t('web.hidden_done'))}>
                    {r.state === 'pending' ? t('web.reject') : t('web.hide')}
                  </Button>
                ) : null}
              </footer>
            ) : null}
          </article>
        ))}
        {mod && tp.state !== 'hidden' ? (
          <section className="card web-reply-box">
            <Field label={t('web.your_reply')}>
              <textarea className="input" rows={4} maxLength={5000} value={reply} onChange={(e) => setReply(e.target.value)} />
            </Field>
            <div className="web-reply-foot">
              <label className="check-row">
                <input type="checkbox" checked={asAnswer} onChange={(e) => setAsAnswer(e.target.checked)} />
                <span>{t('web.mark_as_answer')}</span>
              </label>
              <Button icon="send" busy={busy} disabled={!reply.trim()} onClick={() => void send()}>
                {tp.state === 'pending' ? t('web.reply_and_through') : t('web.post_reply')}
              </Button>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}

// ---- messages ------------------------------------------------------------------------------

function MessagesTab({ onChange }: { onChange: () => void }) {
  const [state, setState] = useState<'' | 'new' | 'handled' | 'spam'>('new');
  const { data, reload } = useAction<{ items: MessageRow[]; can_lead: boolean }>('website.messages', { state: state || null });
  const [open, setOpen] = useState<MessageRow | null>(null);
  const { run, busy } = useSave();
  const changed = async () => {
    await reload();
    onChange();
  };
  const handle = async (m: MessageRow, next: MessageRow['state'], done: string, lead?: string) => {
    if (await run(() => action('website.handle_message', { id: m.id, state: next, ...(lead ? { lead } : {}) }), done)) {
      setOpen(null);
      await changed();
    }
  };
  const makeLead = async (m: MessageRow) => {
    let lead = '';
    const ok = await run(async () => {
      lead = (
        await action<{ id: string }>('crm.save_lead', {
          title: (m.subject || t('web.lead_title', { name: m.name })).slice(0, 160),
          contact_name: m.name.slice(0, 120),
          email: m.email,
          phone: m.phone,
          source: 'web',
          note: m.body.slice(0, 2000),
        })
      ).id;
    });
    if (ok) await handle(m, 'handled', t('web.lead_made'), lead);
  };
  return (
    <section className="card">
      <header className="card-head">
        <div>
          <h2>{t('web.messages')}</h2>
          <span className="muted">{t('web.messages_hint')}</span>
        </div>
        <div className="segmented" role="radiogroup" aria-label={t('web.show')}>
          {(['new', 'handled', 'spam', ''] as const).map((s) => (
            <button key={s} role="radio" aria-checked={state === s} className={state === s ? 'active' : ''} onClick={() => setState(s)}>
              {t(`web.msg_${s || 'all'}` as MessageKey)}
            </button>
          ))}
        </div>
      </header>
      {!data ? (
        <Skeleton h={200} />
      ) : !data.items.length ? (
        <EmptyState icon="inbox" title={state === 'new' ? t('web.inbox_zero') : t('web.no_messages')} body={state === 'new' ? t('web.inbox_zero_body') : undefined} />
      ) : (
        <TableWrap>
          <Table
            head={[t('web.c_from'), t('web.c_message'), t('web.c_received'), t('web.c_state')]}
            phoneHide={[2]}
            left={[1]}
            rows={data.items.map((m) => ({
              key: m.id,
              onClick: () => setOpen(m),
              cells: [
                <span key="f" className="web-from">
                  <strong>{m.name}</strong>
                  <span className="muted small">{m.email ?? m.phone}</span>
                </span>,
                <span key="m" className="web-msg-cell">
                  {m.subject ? <strong>{m.subject}</strong> : null}
                  <span className="muted web-clamp">{m.body}</span>
                </span>,
                <span key="r" className="muted">
                  {dateTime(m.created_at)}
                </span>,
                <StatusBadge
                  key="s"
                  tone={m.state === 'new' ? 'warn' : m.state === 'handled' ? 'ok' : 'neutral'}
                  icon={m.state === 'new' ? 'mail' : m.state === 'handled' ? 'check' : 'alert'}
                  label={t(`web.m_${m.state}` as MessageKey)}
                />,
              ],
            }))}
          />
        </TableWrap>
      )}
      {open ? (
        <Dialog
          title={open.subject || t('web.message_from', { name: open.name })}
          onClose={() => setOpen(null)}
          size="lg"
          footer={
            <>
              {open.state !== 'spam' ? (
                <Button variant="ghost" icon="alert" busy={busy} onClick={() => void handle(open, 'spam', t('web.marked_spam'))}>
                  {t('web.spam')}
                </Button>
              ) : (
                <Button
                  variant="danger"
                  icon="trash"
                  busy={busy}
                  onClick={() =>
                    void run(() => action('website.remove_message', { id: open.id }), t('web.message_removed')).then(async (ok) => {
                      if (ok) {
                        setOpen(null);
                        await changed();
                      }
                    })
                  }
                >
                  {t('web.remove')}
                </Button>
              )}
              {open.state === 'new' && data?.can_lead ? (
                <Button variant="ghost" icon="chart" busy={busy} onClick={() => void makeLead(open)}>
                  {t('web.make_lead')}
                </Button>
              ) : null}
              {open.state === 'new' ? (
                <Button icon="check" busy={busy} onClick={() => void handle(open, 'handled', t('web.handled_done'))}>
                  {t('web.mark_handled')}
                </Button>
              ) : (
                <Button variant="ghost" icon="undo" busy={busy} onClick={() => void handle(open, 'new', t('web.back_to_new'))}>
                  {t('web.mark_new')}
                </Button>
              )}
            </>
          }
        >
          <dl className="web-msg-meta">
            <dt>{t('web.c_from')}</dt>
            <dd>{open.name}</dd>
            {open.email ? (
              <>
                <dt>{t('web.email')}</dt>
                <dd>
                  <a href={`mailto:${open.email}?subject=${encodeURIComponent(`Re: ${open.subject ?? ''}`)}`}>{open.email}</a>
                </dd>
              </>
            ) : null}
            {open.phone ? (
              <>
                <dt>{t('web.phone')}</dt>
                <dd>
                  <a href={`tel:${open.phone.replace(/\s/g, '')}`}>{open.phone}</a>
                </dd>
              </>
            ) : null}
            <dt>{t('web.c_received')}</dt>
            <dd>
              {dateTime(open.created_at)} · {messageName(open.number)}
              {open.page ? ` · /${open.page}` : ''}
            </dd>
            {open.handled_by_name ? (
              <>
                <dt>{t('web.handled_by')}</dt>
                <dd>
                  {open.handled_by_name} · {open.handled_at ? dateTime(open.handled_at) : ''}
                </dd>
              </>
            ) : null}
            {open.lead ? (
              <>
                <dt>{t('web.opportunity')}</dt>
                <dd>
                  <Link to={`/crm?lead=${open.lead}`}>{t('web.open_opportunity')}</Link>
                </dd>
              </>
            ) : null}
          </dl>
          <div className="web-msg-body">
            <RichText text={open.body} />
          </div>
          {open.email ? (
            <a className="btn btn-ghost" href={`mailto:${open.email}?subject=${encodeURIComponent(`Re: ${open.subject ?? ''}`)}`}>
              <Icon name="mail" size={18} />
              <span className="btn-label">{t('web.answer_by_email')}</span>
            </a>
          ) : null}
        </Dialog>
      ) : null}
    </section>
  );
}

const messageName = (n: number) => `WM-${String(n).padStart(4, '0')}`;

// ---- settings ------------------------------------------------------------------------------

function SettingsTab({ o, slug, onSaved }: { o: Overview; slug: string; onSaved: () => void }) {
  const [s, setS] = useState<Settings>(o.settings);
  const [dirty, setDirty] = useState(false);
  const { run, busy } = useSave();
  const can = o.can.settings;
  const set = <K extends keyof Settings>(k: K, v: Settings[K]) => {
    setS({ ...s, [k]: v });
    setDirty(true);
  };
  const save = async () => {
    const { id: _id, ...input } = s;
    const clean = Object.fromEntries(Object.entries(input).map(([k, v]) => [k, typeof v === 'string' ? v.trim() || null : v]));
    if (await run(() => action('website.save_settings', clean), t('web.saved'))) {
      setDirty(false);
      onSaved();
    }
  };
  const parts: { key: 'blog' | 'forum' | 'moderate' | 'contact_form' | 'show_shop' | 'show_careers'; show: boolean }[] = useMemo(
    () => [
      { key: 'blog', show: true },
      { key: 'forum', show: true },
      { key: 'moderate', show: s.forum },
      { key: 'contact_form', show: true },
      { key: 'show_shop', show: true },
      { key: 'show_careers', show: true },
    ],
    [s.forum],
  );
  return (
    <div className="online-settings">
      <section className="card">
        <header className="card-head">
          <div>
            <h2>
              <Icon name="globe" size={20} /> {t('web.s_site')}
            </h2>
            <span className="muted">{s.enabled ? t('web.s_on_hint', { url: siteUrl(slug) }) : t('web.s_off_hint')}</span>
          </div>
          <Switch checked={s.enabled} onChange={(v) => set('enabled', v)} label={t('web.s_enabled')} disabled={!can} />
        </header>
        <div className="form-grid">
          <Field label={t('web.s_title')} hint={t('web.s_title_hint', { name: o.business })}>
            <input className="input" value={s.title ?? ''} maxLength={80} disabled={!can} onChange={(e) => set('title', e.target.value)} />
          </Field>
          <Field label={t('web.s_tagline')} hint={t('web.s_tagline_hint')}>
            <input className="input" value={s.tagline ?? ''} maxLength={200} disabled={!can} onChange={(e) => set('tagline', e.target.value)} />
          </Field>
        </div>
        <fieldset className="option-group">
          <legend>{t('web.s_colour')}</legend>
          <div className="tone-picker" role="radiogroup" aria-label={t('web.s_colour')}>
            {TONES.map((tone) => (
              <button
                key={tone}
                role="radio"
                aria-checked={s.accent === tone}
                disabled={!can}
                className={`tone-dot tone-${tone}${s.accent === tone ? ' active' : ''}`}
                onClick={() => set('accent', tone)}
                aria-label={t(`tone.${tone}` as MessageKey)}
                title={t(`tone.${tone}` as MessageKey)}
              >
                {s.accent === tone ? <Icon name="check" size={16} /> : null}
              </button>
            ))}
          </div>
        </fieldset>
        <div className="form-grid">
          <Field label={t('web.s_hours')} hint={t('web.s_hours_hint')}>
            <textarea className="input" rows={3} value={s.hours ?? ''} maxLength={600} disabled={!can} onChange={(e) => set('hours', e.target.value)} />
          </Field>
          <Field label={t('web.s_footer')} hint={t('web.s_footer_hint')}>
            <textarea className="input" rows={3} value={s.footer ?? ''} maxLength={600} disabled={!can} onChange={(e) => set('footer', e.target.value)} />
          </Field>
        </div>
        <p className="muted small">
          <Icon name="note" size={14} /> {t('web.s_contact_from_company')} <Link to="/manage/business">{t('web.s_company_link')}</Link>
        </p>
      </section>
      <section className="card">
        <header className="card-head">
          <div>
            <h2>
              <Icon name="layers" size={20} /> {t('web.s_parts')}
            </h2>
            <span className="muted">{t('web.s_parts_hint')}</span>
          </div>
        </header>
        {parts
          .filter((p) => p.show)
          .map((p) => (
            <div key={p.key} className={`setting-row${p.key === 'moderate' ? ' indent' : ''}`}>
              <div>
                <strong>{t(`web.s_${p.key}` as MessageKey)}</strong>
                <p className="muted">{t(`web.s_${p.key}_hint` as MessageKey)}</p>
              </div>
              <Switch checked={s[p.key]} onChange={(v) => set(p.key, v)} label={t(`web.s_${p.key}` as MessageKey)} disabled={!can} />
            </div>
          ))}
      </section>
      <section className="card">
        <header className="card-head">
          <div>
            <h2>
              <Icon name="share" size={20} /> {t('web.s_social')}
            </h2>
            <span className="muted">{t('web.s_social_hint')}</span>
          </div>
        </header>
        <div className="form-grid">
          {(['facebook', 'instagram', 'tiktok', 'linkedin'] as const).map((k) => (
            <Field key={k} label={t(`web.s_${k}` as MessageKey)}>
              <input className="input" type="url" placeholder="https://" value={s[k] ?? ''} maxLength={200} disabled={!can} onChange={(e) => set(k, e.target.value)} />
            </Field>
          ))}
        </div>
      </section>
      {can && dirty ? (
        <div className="sticky-save">
          <span className="muted">{t('web.unsaved_changes')}</span>
          <Button icon="check" busy={busy} disabled={!dirty} onClick={() => void save()}>
            {t('web.save')}
          </Button>
        </div>
      ) : (
        <p className="callout callout-info">{t('web.s_manager_only')}</p>
      )}
    </div>
  );
}
